// @ts-strict-ignore
import shortid from 'shortid';
import type {
  SarActivityInstitution,
  SarEntity,
  SarFilingInstitution,
  SarMetadata,
  SarMetadatum,
  SarReport,
  SarWizard,
} from '~/dashboard/views/sars/form-view/types';
import {
  QUESTION_32_CHECKBOX_INITIAL,
  QUESTION_33_CHECKBOX_INITIAL,
  QUESTION_34_CHECKBOX_INITIAL,
  QUESTION_35_CHECKBOX_INITIAL,
  QUESTION_36_CHECKBOX_INITIAL,
  QUESTION_37_CHECKBOX_INITIAL,
  QUESTION_38_CHECKBOX_INITIAL,
  QUESTION_39_CHECKBOX_INITIAL,
  QUESTION_40_CHECKBOX_INITIAL,
  QUESTION_41_CHECKBOX_INITIAL,
  QUESTION_42_CHECKBOX_INITIAL,
  QUESTION_45_CHECKBOX_INITIAL,
  QUESTION_46_CHECKBOX_INITIAL,
  QUESTION_53_CHECKBOX_INITIAL,
  QUESTION_54_CHECKBOX_INITIAL,
  QUESTION_56_CHECKBOX_INITIAL,
  QUESTION_68_CHECKBOX_INITIAL,
  QUESTION_80_CHECKBOX_INITIAL,
  SAR_ENTITY_INITIAL,
} from '~/dashboard/views/sars/form-view/constants/form.constant';

export type SarState = {
  error: any;
  inFlightRequests: number;
  report: SarReport | null;
  status: 'loading' | 'success' | 'failed';
  wizard: SarWizard | null;
  xml: XMLDocument | null;
  metadata: SarMetadata;
  deletedPages: {
    report: { entities: SarEntity[] };
    metadata: { entities: Array<{ [prop: string]: Array<SarMetadatum> }> };
  };
};

export const initialMetadataShape = () => (<SarMetadatum>{
  is_unknown: false,
  errors: {},
  key: shortid.generate(),
});

export const initialSarMetadataGenerators: {
  [pageKey: string]: () => { [prop: string]: SarMetadatum[] | SarMetadatum[][] };
} = {
  entities: () => ({
    address_subheader: [{ ...initialMetadataShape() }],
    financial_institution_subheader: [{ ...initialMetadataShape() }],
    id_subheader: [{ ...initialMetadataShape() }],
    relationship_subheader: [{ ...initialMetadataShape() }],
    subject_address_additive: [{ ...initialMetadataShape() }],
    question_3_entity: [{ ...initialMetadataShape() }],
    question_3_pii_unavailable: [{ ...initialMetadataShape() }],
    question_4_text: [{ ...initialMetadataShape() }],
    question_5_text: [{ ...initialMetadataShape() }],
    question_6: [{ ...initialMetadataShape() }],
    question_7_text: [{ ...initialMetadataShape() }],
    question_8_dropdown: [{ ...initialMetadataShape() }],
    question_9: [{ ...initialMetadataShape() }],
    question_9_additive: [{ ...initialMetadataShape() }],
    question_10: [{ ...initialMetadataShape() }],
    question_10a: [{ ...initialMetadataShape() }],
    question_11_address: [{ ...initialMetadataShape() }],
    question_12_city: [{ ...initialMetadataShape() }],
    question_13_state: [{ ...initialMetadataShape() }],
    question_14_zip: [{ ...initialMetadataShape() }],
    question_15_country: [{ ...initialMetadataShape() }],
    question_16_text: [{ ...initialMetadataShape() }],
    question_17: [{ ...initialMetadataShape() }],
    question_18_additive: [{ ...initialMetadataShape() }],
    question_18_country: [{ ...initialMetadataShape() }],
    question_18_other: [{ ...initialMetadataShape() }],
    question_18_number: [{ ...initialMetadataShape() }],
    question_18_state: [{ ...initialMetadataShape() }],
    question_18_type: [{ ...initialMetadataShape() }],
    question_19_text: [{ ...initialMetadataShape() }],
    question_21_additive: [{ ...initialMetadataShape() }],
    question_21_value: [{ ...initialMetadataShape() }],
    question_21_ext: [{ ...initialMetadataShape() }],
    question_21_type: [{ ...initialMetadataShape() }],
    question_22: [{ ...initialMetadataShape() }],
    question_22_additive: [{ ...initialMetadataShape() }],
    question_22a: [{ ...initialMetadataShape() }],
    question_22a_additive: [{ ...initialMetadataShape() }],
    question_23_dropdown: [{ ...initialMetadataShape() }],
    question_24_additive: [{ ...initialMetadataShape() }],
    question_24_checkbox: [{ ...initialMetadataShape() }],
    question_24_intitution_tin: [{ ...initialMetadataShape() }],
    question_24_other: [{ ...initialMetadataShape() }],
    question_25: [{ ...initialMetadataShape() }],
    question_25_relationship: [{ ...initialMetadataShape() }],
    question_26_action_date: [{ ...initialMetadataShape() }],
    question_27_additive: [{ ...initialMetadataShape() }],
    question_27_account_number: [[{ ...initialMetadataShape() }]],
    question_27_account_numbers: [[{ ...initialMetadataShape() }]],
    question_27_account_numbers_additive: [[{ ...initialMetadataShape() }]],
    question_27_tin: [{ ...initialMetadataShape() }],
    question_27_tins: [{ ...initialMetadataShape() }],
    question_28_dropdown: [{ ...initialMetadataShape() }],
  }),
};

export const initialSarMetadata: SarMetadata = {
  is_ninety_day_review: [{ ...initialMetadataShape() }],
  notification_days: [{ ...initialMetadataShape() }],
  entities: [{ ...initialSarMetadataGenerators.entities() }],
  activity_information: {
    question_2: [{ ...initialMetadataShape() }],
    question_29_amount: [{ ...initialMetadataShape() }],
    question_29_no_amount: [{ ...initialMetadataShape() }],
    question_30_from: [{ ...initialMetadataShape() }],
    question_30_to: [{ ...initialMetadataShape() }],
    question_31: [{ ...initialMetadataShape() }],
    question_32: [{ ...initialMetadataShape() }],
    question_32_other: [{ ...initialMetadataShape() }],
    question_33: [{ ...initialMetadataShape() }],
    question_33_other: [{ ...initialMetadataShape() }],
    question_34: [{ ...initialMetadataShape() }],
    question_34_other: [{ ...initialMetadataShape() }],
    question_35: [{ ...initialMetadataShape() }],
    question_35_other: [{ ...initialMetadataShape() }],
    question_36: [{ ...initialMetadataShape() }],
    question_36_other: [{ ...initialMetadataShape() }],
    question_37: [{ ...initialMetadataShape() }],
    question_37_other: [{ ...initialMetadataShape() }],
    question_38: [{ ...initialMetadataShape() }],
    question_38_other: [{ ...initialMetadataShape() }],
    question_39: [{ ...initialMetadataShape() }],
    question_39_other: [{ ...initialMetadataShape() }],
    question_40: [{ ...initialMetadataShape() }],
    question_40_other: [{ ...initialMetadataShape() }],
    question_41: [{ ...initialMetadataShape() }],
    question_41_other: [{ ...initialMetadataShape() }],
    question_42: [{ ...initialMetadataShape() }],
    question_42_other: [{ ...initialMetadataShape() }],
    ip_address_subheader: [{ ...initialMetadataShape() }],
    question_43_additive: [{ ...initialMetadataShape() }],
    question_43_date: [{ ...initialMetadataShape() }],
    question_43_ip_address: [{ ...initialMetadataShape() }],
    question_43_timestamp: [{ ...initialMetadataShape() }],
    cyber_event_subheader: [{ ...initialMetadataShape() }],
    question_44: [{ ...initialMetadataShape() }],
    question_44_additive: [{ ...initialMetadataShape() }],
    question_44_event_date: [{ ...initialMetadataShape() }],
    question_44_event_type: [{ ...initialMetadataShape() }],
    question_44_event_type_other: [{ ...initialMetadataShape() }],
    question_44_event_value: [{ ...initialMetadataShape() }],
    question_45: [{ ...initialMetadataShape() }],
    question_45_other: [{ ...initialMetadataShape() }],
    question_46: [{ ...initialMetadataShape() }],
    question_46_other: [{ ...initialMetadataShape() }],
    question_47: [{ ...initialMetadataShape() }],
    question_47_additive: [{ ...initialMetadataShape() }],
    question_48: [{ ...initialMetadataShape() }],
    question_48_additive: [{ ...initialMetadataShape() }],
    question_49: [{ ...initialMetadataShape() }],
    question_49_additive: [{ ...initialMetadataShape() }],
    question_50: [{ ...initialMetadataShape() }],
    question_50_additive: [{ ...initialMetadataShape() }],
    narrative: [{ ...initialMetadataShape() }],
  },
  sar_activity_institution: {
    question_51_type: [{ ...initialMetadataShape() }],
    question_51_other: [{ ...initialMetadataShape() }],
    question_52: [{ ...initialMetadataShape() }],
    question_53: [{ ...initialMetadataShape() }],
    question_53_other: [{ ...initialMetadataShape() }],
    question_54: [{ ...initialMetadataShape() }],
    question_54_other: [{ ...initialMetadataShape() }],
    question_55_number: [{ ...initialMetadataShape() }],
    question_55_type: [{ ...initialMetadataShape() }],
    question_57: [{ ...initialMetadataShape() }],
    question_58: [{ ...initialMetadataShape() }],
    question_59_tin: [{ ...initialMetadataShape() }],
    question_60: [{ ...initialMetadataShape() }],
    question_61_address: [{ ...initialMetadataShape() }],
    question_62_city: [{ ...initialMetadataShape() }],
    question_63_state: [{ ...initialMetadataShape() }],
    question_64_zip: [{ ...initialMetadataShape() }],
    question_65_country: [{ ...initialMetadataShape() }],
    question_66: [{ ...initialMetadataShape() }],
    question_67: [{ ...initialMetadataShape() }],
    branch_information_additive: [{ ...initialMetadataShape() }],
    branch_subheader: [{ ...initialMetadataShape() }],
    question_68_role: [{ ...initialMetadataShape() }],
    question_69_address: [{ ...initialMetadataShape() }],
    question_70_rssd: [{ ...initialMetadataShape() }],
    question_71: [{ ...initialMetadataShape() }],
    question_71_city: [{ ...initialMetadataShape() }],
    question_72: [{ ...initialMetadataShape() }],
    question_72_state: [{ ...initialMetadataShape() }],
    question_73_zip: [{ ...initialMetadataShape() }],
    question_74_country: [{ ...initialMetadataShape() }],
  },
  filing_institution: {
    question_75: [{ ...initialMetadataShape() }],
    question_76: [{ ...initialMetadataShape() }],
    question_77_tin: [{ ...initialMetadataShape() }],
    question_78: [{ ...initialMetadataShape() }],
    question_79_type: [{ ...initialMetadataShape() }],
    question_79_other: [{ ...initialMetadataShape() }],
    question_80: [{ ...initialMetadataShape() }],
    question_80_other: [{ ...initialMetadataShape() }],
    question_81_number: [{ ...initialMetadataShape() }],
    question_81_type: [{ ...initialMetadataShape() }],
    question_82_address: [{ ...initialMetadataShape() }],
    question_83_city: [{ ...initialMetadataShape() }],
    question_84_state: [{ ...initialMetadataShape() }],
    question_85_zip: [{ ...initialMetadataShape() }],
    question_86_country: [{ ...initialMetadataShape() }],
    question_87: [{ ...initialMetadataShape() }],
    question_88: [{ ...initialMetadataShape() }],
    question_89: [{ ...initialMetadataShape() }],
    question_90: [{ ...initialMetadataShape() }],
    question_91: [{ ...initialMetadataShape() }],
    question_91_ext: [{ ...initialMetadataShape() }],
    question_92: [{ ...initialMetadataShape() }],
    question_93: [{ ...initialMetadataShape() }],
    question_94: [{ ...initialMetadataShape() }],
    question_94_ext: [{ ...initialMetadataShape() }],
  },
};

export const initialFraudTypeObject = initialData => ({
  ...initialData,
  other_value: '',
});

export const initialFraudTypes = () => ({
  structuring: initialFraudTypeObject(QUESTION_32_CHECKBOX_INITIAL),
  terr_fin: initialFraudTypeObject(QUESTION_33_CHECKBOX_INITIAL),
  fraud: initialFraudTypeObject(QUESTION_34_CHECKBOX_INITIAL),
  gaming: initialFraudTypeObject(QUESTION_35_CHECKBOX_INITIAL),
  laundering: initialFraudTypeObject(QUESTION_36_CHECKBOX_INITIAL),
  documentation: initialFraudTypeObject(QUESTION_37_CHECKBOX_INITIAL),
  misc: initialFraudTypeObject(QUESTION_38_CHECKBOX_INITIAL),
  insurance: initialFraudTypeObject(QUESTION_39_CHECKBOX_INITIAL),
  securities: initialFraudTypeObject(QUESTION_40_CHECKBOX_INITIAL),
  mortgage: initialFraudTypeObject(QUESTION_41_CHECKBOX_INITIAL),
  cyber: initialFraudTypeObject(QUESTION_42_CHECKBOX_INITIAL),
  product: initialFraudTypeObject(QUESTION_45_CHECKBOX_INITIAL),
  instrument: initialFraudTypeObject(QUESTION_46_CHECKBOX_INITIAL),
});

export const initialState: SarState = {
  error: null,
  inFlightRequests: 0,
  report: null,
  status: 'loading',
  wizard: null,
  xml: null,
  metadata: initialSarMetadata,
  deletedPages: {
    report: { entities: [] },
    metadata: { entities: [] },
  },
};

export const initialSarActivityInstitution: SarActivityInstitution = {
  is_filing_institution: false,
  type_of_financial_institution: [''],
  primary_federal_regulator: '',
  legal_name: '',
  tin: '',
  tin_type: '',
  type_of_securities_institution: {
    checkboxes: { ...QUESTION_54_CHECKBOX_INITIAL },
    other: '',
  },
  institution_id: '',
  institution_id_type: '',
  address: {
    address: '',
    city: '',
    state: '',
    country: '',
    zip: '',
  },
  alternate_name: '',
  internal_control_number: '',
  type_of_gaming_institution: {
    checkboxes: { ...QUESTION_53_CHECKBOX_INITIAL },
    other: '',
  },
  financial_institution_role: { ...QUESTION_56_CHECKBOX_INITIAL },
  loss_financial_institution: null,
  branch_information: [{
    address: '',
    branch_role: { ...QUESTION_68_CHECKBOX_INITIAL },
    city: '',
    country: '',
    rssd_number: '',
    state: '',
    zip: '',
  }],
  branch_activity: false,
  no_branch_activity: false,
};

export const initialSarFilingInstitution: SarFilingInstitution = {
  is_filing_institution: true,
  type_of_financial_institution: [''],
  primary_federal_regulator: '',
  filer_name: '',
  tin: '',
  tin_type: '',
  type_of_securities_institution: { ...QUESTION_80_CHECKBOX_INITIAL },
  institution_id: '',
  institution_id_type: '',
  address: {
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
  alternate_name: '',
  internal_control_number: '',
  contact_agency: '',
  contact_name: '',
  phone_number: ['', ''],
  contact_date: '',
  filing_institution_contact_office: '',
  filing_institution_contact_number: ['', ''],
};

export const initialSarEntity: () => SarEntity = () => (SAR_ENTITY_INITIAL);

export const initialDeletedPages = {
  report: { entities: [] },
  metadata: { entities: [] },
};
